// Stress & Burnout self-check — ORIGINAL wording (NOT the Perceived Stress Scale,
// which is exclusively distributed by Mapi Trust). Non-diagnostic. 15 items across
// the WHO ICD-11 burnout dimensions (exhaustion, mental distance/cynicism, reduced
// efficacy) plus a body & coping dimension. Band scoring: each option 0–3, summed
// across 15 questions (max 45) → 3 bands (thirds).
const stress = {
  slug: "stress",
  title: "Are you stressed — or heading for burnout?",
  emoji: "🔥",
  blurb: "A 15-item self-check mapped to the WHO burnout dimensions, with the adaptogens that fit your score.",
  seoTitle: "Stress & Burnout Quiz: How Stressed Are You? | Sarv",
  seoDescription:
    "Take the free stress and burnout self-check to see where you land — balanced, under pressure, or running on empty — plus the adaptogenic herbs traditionally used for each.",
  keywords:
    "stress quiz, burnout quiz, am I burned out, stress test, adaptogens for stress, natural remedies for stress",
  intro:
    "Over the last two weeks, how often has each been true? Grouped around the WHO burnout dimensions. A general wellness check, not a medical diagnosis.",
  evidenceNote:
    "The dimensions this check measures — exhaustion, mental distance/cynicism, and reduced effectiveness — are exactly how the World Health Organization defines burnout in the ICD-11. Chronic stress is well documented to affect the body, and adaptogens such as ashwagandha, rhodiola, and holy basil (tulsi) have randomized-controlled and systematic-review evidence behind their traditional use. This is a wellness self-check, not a diagnostic test.",
  references: [
    {
      label: "Burn-out an 'occupational phenomenon': International Classification of Diseases (ICD-11)",
      publisher: "World Health Organization",
      url: "https://www.who.int/news/item/28-05-2019-burn-out-an-occupational-phenomenon-international-classification-of-diseases",
    },
    {
      label: "Adaptogenic and anxiolytic effects of ashwagandha root extract: a randomized, placebo-controlled study",
      publisher: "PMC / NIH",
      url: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6979308/",
    },
    {
      label: "Ashwagandha: Is it helpful for stress, anxiety, or sleep? (health-professional fact sheet)",
      publisher: "NIH Office of Dietary Supplements",
      url: "https://ods.od.nih.gov/factsheets/Ashwagandha-HealthProfessional/",
    },
    {
      label: "Rhodiola rosea in life-stress symptoms and stress-induced conditions: clinical evidence",
      publisher: "PMC / NIH",
      url: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC9228580/",
    },
    {
      label: "The clinical efficacy and safety of tulsi (holy basil) in humans: a systematic review",
      publisher: "PubMed / NIH",
      url: "https://pubmed.ncbi.nlm.nih.gov/28400848/",
    },
  ],
  scoring: {
    mode: "band",
    max: 45,
    // ascending by max; total <= max picks the band (thirds of 45)
    bands: [
      { max: 15, key: "balanced" },
      { max: 30, key: "stressed" },
      { max: 45, key: "burnout" },
    ],
  },
  // Every question shares the same 0–3 frequency scale.
  questions: [
    { section: "Exhaustion", question: "I've felt on edge or unable to relax." },
    { section: "Exhaustion", question: "I've felt physically drained, even after resting." },
    { section: "Exhaustion", question: "I've woken up already tired, dreading the day ahead." },
    { section: "Exhaustion", question: "I've felt emotionally worn out by my responsibilities." },
    { section: "Mental distance", question: "I've felt cynical, detached, or 'checked out' from work or daily life." },
    { section: "Mental distance", question: "I've been more irritable, impatient, or short-tempered than usual." },
    { section: "Mental distance", question: "I've lost interest or motivation in things I usually enjoy." },
    { section: "Mental distance", question: "I've felt emotionally numb or distant from people around me." },
    { section: "Effectiveness", question: "I've felt I'm accomplishing less, or that my performance has slipped." },
    { section: "Effectiveness", question: "I've struggled to concentrate or felt mentally foggy." },
    { section: "Effectiveness", question: "I've doubted whether my efforts really make a difference." },
    { section: "Body & coping", question: "I've had physical stress signs — headaches, tension, a churning stomach, or disturbed sleep." },
    { section: "Body & coping", question: "I've found it hard to switch off from work or responsibilities." },
    { section: "Body & coping", question: "I've leaned on caffeine, sugar, alcohol, or screens to get through the day." },
    { section: "Body & coping", question: "I've found it hard to recover or unwind, even on days off." },
  ].map((q) => ({
    section: q.section,
    question: q.question,
    options: [
      { label: "Rarely or never", value: 0 },
      { label: "Sometimes", value: 1 },
      { label: "Often", value: 2 },
      { label: "Almost every day", value: 3 },
    ],
  })),
  results: {
    balanced: {
      name: "Balanced & Coping",
      emoji: "🌿",
      tagline: "You're managing your stress load well right now.",
      summary:
        "Your responses suggest you're carrying a manageable stress load and coping reasonably well — you're not showing the exhaustion, detachment, or drop in effectiveness that mark heavier strain. That's genuinely worth protecting rather than taking for granted, because resilience is far easier to maintain than to rebuild once it's been lost. The aim here is preventive: keeping the fundamentals strong (sleep, movement, real downtime and connection), staying alert to your own early warning signs, and optionally leaning on gentle, everyday adaptogens traditionally used to help the body stay steady under pressure. Think of this as topping up your reserves so a genuinely demanding stretch doesn't tip you into overload.",
      focus: [
        "Keep the basics strong: sleep, movement, and real downtime.",
        "Gentle, everyday adaptogens traditionally used to build resilience.",
        "Notice early warning signs before stress builds.",
      ],
      herbs: ["Holy basil (tulsi)", "Green tea (L-theanine)", "Ashwagandha"],
      categorySlug: "mental-and-cognitive-function",
    },
    stressed: {
      name: "Under Pressure",
      emoji: "⚠️",
      tagline: "Stress is building — a good moment to intervene.",
      summary:
        "Your responses suggest a meaningful stress load that's beginning to show — in how you feel, focus, and recover. This is exactly the stage where intervening pays off most, before pressure hardens into burnout. Chronic stress keeps cortisol elevated and the nervous system stuck in 'go' mode, which is why sleep, mood, and concentration are usually the first things to slip. The most effective approach is twofold: build genuine daily decompression (breathwork, walks, a real wind-down that isn't a screen) and use adaptogenic herbs traditionally used to steady the stress response and support the body's resilience. Protecting sleep is non-negotiable here — it's both the first casualty of stress and one of your strongest tools against it.",
      focus: [
        "Build daily decompression — breathwork, walks, or a wind-down ritual.",
        "Adaptogenic herbs traditionally used to steady the stress response.",
        "Protect sleep, which stress erodes first and fastest.",
      ],
      herbs: ["Ashwagandha", "Rhodiola", "Holy basil (tulsi)"],
      categorySlug: "mental-and-cognitive-function",
    },
    burnout: {
      name: "Running on Empty",
      emoji: "🔋",
      tagline: "Your responses point to a high stress load and low reserves.",
      summary:
        "Your responses point to a high stress load with low reserves — the pattern that maps onto what the WHO describes as burnout: exhaustion, growing cynicism or detachment, and a sense that you're accomplishing less. This tends to build over months of demand outstripping recovery, so the way out is recovery-led rather than push-through — the instinct to just work harder is what deepens it. The priority is genuine, protected rest, firmer boundaries, and support, paired with restorative, adaptogenic herbs traditionally used when the body's reserves are running low. Importantly, burnout overlaps with conditions like depression and thyroid problems, so if low mood, exhaustion, or overwhelm are affecting your daily life, please treat this result as a nudge to also talk with a healthcare professional. This isn't something you have to power through alone.",
      focus: [
        "Prioritize recovery: protected rest, boundaries, and support.",
        "Restorative adaptogens traditionally used when reserves run low.",
        "Consider talking to a professional — this isn't something to push through alone.",
      ],
      herbs: ["Ashwagandha", "Rhodiola", "Reishi", "Licorice root"],
      categorySlug: "mental-and-cognitive-function",
    },
  },
};

export default stress;
